// Admin panel — per-service VTU provider switcher.
// Updated: each service (Airtime, Data, Electricity, TV) can use
// a different provider independently.
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getVTUProvider, setVTUProvider, testVTUProvider } from "../../../api/apiServices";

// ─── Static metadata ──────────────────────────────────────────
const SERVICE_META = {
  airtime:     { icon: "ri-phone-fill",      label: "Airtime",     color: "#F59E0B" },
  data:        { icon: "ri-wifi-line",        label: "Data",        color: "#0EA5E9" },
  electricity: { icon: "ri-flashlight-fill",  label: "Electricity", color: "#F97316" },
  tv:          { icon: "ri-tv-2-fill",        label: "Cable TV",    color: "#8B5CF6" },
};

const PROVIDER_META = {
  vtpass:         { label: "VTPass",         color: "#059669", icon: "ri-server-line",   website: "vtpass.com"         },
  paygold:        { label: "Paygold",        color: "#0284C7", icon: "ri-bank-card-line", website: "paygold.ng"         },
  vtung:          { label: "VTU.ng",         color: "#7C3AED", icon: "ri-shield-line",   website: "vtu.ng"             },
  airtimenigeria: { label: "AirtimeNigeria", color: "#DC2626", icon: "ri-phone-fill",    website: "airtimenigeria.com" },

   strowallet: { label: "Strowallet", color: "#0EA5E9", icon: "ri-wallet-3-line",    website: "https://strowallet.com" },
};

// ── Small coloured pill showing which provider is active ──────
function ProviderBadge({ providerKey }) {
  const m = PROVIDER_META[providerKey] || { label: providerKey, color: "#6B7280" };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
      style={{ background: m.color + "20", color: m.color }}>
      <i className={`${m.icon} text-[9px]`} />
      {m.label}
    </span>
  );
}

// ── One provider option inside a service row ──────────────────
function ProviderOption({ providerKey, meta, isActive, isSupported, onSelect, switching }) {
  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/4 opacity-25 cursor-not-allowed"
        title="Not supported for this service">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: meta.color + "15" }}>
          <i className={`${meta.icon} text-sm`} style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/40 text-xs font-semibold truncate">{meta.label}</p>
          <p className="text-white/20 text-[9px]">Not supported</p>
        </div>
      </div>
    );
  }

  return (
    <button
      disabled={isActive || switching}
      onClick={() => !isActive && !switching && onSelect(providerKey)}
      className="flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-left w-full"
      style={{
        background:   isActive ? meta.color + "12" : "#1a1a1a",
        borderColor:  isActive ? meta.color + "40" : "rgba(255,255,255,0.06)",
        cursor:       isActive ? "default" : "pointer",
      }}>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: meta.color + "20" }}>
        <i className={`${meta.icon} text-sm`} style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs font-semibold truncate">{meta.label}</p>
        <p className="text-white/30 text-[9px]">{meta.website}</p>
      </div>
      {isActive ? (
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1"
          style={{ background: meta.color + "20", color: meta.color }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: meta.color }} />
          Active
        </span>
      ) : (
        <span className="text-[9px] text-white/25 flex-shrink-0">
          {switching ? "…" : "Use"}
        </span>
      )}
    </button>
  );
}

// ── One full row: service header + its provider options ───────
function ServiceRow({ service, current, options, allProviderKeys, onSwitch, isSwitching }) {
  const svc = SERVICE_META[service] || { icon: "ri-settings-line", label: service, color: "#6B7280" };
  const supportedKeys = options.map(o => o.key);

  return (
    <div className="bg-[#161616] rounded-2xl border border-white/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: svc.color + "15" }}>
          <i className={`${svc.icon} text-base`} style={{ color: svc.color }} />
        </div>
        <div className="flex-1">
          <p className="text-white font-bold text-sm">{svc.label}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-white/30 text-[10px]">Provider:</span>
            <ProviderBadge providerKey={current} />
          </div>
        </div>
      </div>

      {/* Provider options grid — 2 cols on mobile, 4 on sm+ */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {allProviderKeys.map(pk => {
          const meta = PROVIDER_META[pk] || { label: pk, color: "#6B7280", icon: "ri-settings-line", website: "" };
          return (
            <ProviderOption
              key={pk}
              providerKey={pk}
              meta={meta}
              isActive={current === pk}
              isSupported={supportedKeys.includes(pk)}
              onSelect={(key) => onSwitch(service, key)}
              switching={isSwitching}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Per-service test result panel ─────────────────────────────
function TestPanel({ result, onClose }) {
  if (!result) return null;
  return (
    <div className="bg-[#161616] rounded-2xl border border-white/8 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
        <p className="text-white/60 text-xs font-bold uppercase tracking-wide">Provider Test Results</p>
        <button onClick={onClose} className="text-white/30 hover:text-white/60 text-xs">Dismiss</button>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(result).map(([svc, info]) => {
          const svcMeta = SERVICE_META[svc] || { icon: "ri-settings-line", label: svc, color: "#6B7280" };
          const ok      = info.ok;
          return (
            <div key={svc} className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
              ok ? "bg-emerald-500/8 border-emerald-500/20" : "bg-red-500/8 border-red-500/20"
            }`}>
              <i className={`${ok ? "ri-checkbox-circle-line text-emerald-400" : "ri-close-circle-line text-red-400"} text-lg flex-shrink-0`} />
              <div>
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <p className="text-white text-sm font-semibold">{svcMeta.label}</p>
                  <ProviderBadge providerKey={info.provider} />
                </div>
                {ok
                  ? <p className="text-white/40 text-[10px]">{info.planCount} plans · {info.responseMs}ms</p>
                  : <p className="text-red-400/70 text-[10px]">{info.error || "Failed"}</p>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function VTUProviderView() {
  const [data,        setData]        = useState(null);   // { services[], allProviders[] }
  const [loading,     setLoading]     = useState(true);
  const [switching,   setSwitching]   = useState(null);   // service name that is switching
  const [testing,     setTesting]     = useState(false);
  const [testResult,  setTestResult]  = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getVTUProvider();
      if (res.status) setData(res.data);
    } catch { toast.error("Failed to load provider info"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Switch provider for one service OR all services at once
  const handleSwitch = async (service, provider) => {
    setSwitching(service);
    try {
      // Updated API: { service, provider } — supports "all" or a single service name
      const res = await setVTUProvider({ service, provider });
      if (res.status) {
        toast.success(res.message);
        setTestResult(null);
        // Update local state
        setData(prev => ({
          ...prev,
          services: prev.services.map(s => {
            const shouldUpdate = service === "all" || s.service === service;
            if (!shouldUpdate) return s;
            // Only update if this provider supports this service
            const isSupported = s.options.some(o => o.key === provider);
            if (!isSupported) return s;
            return { ...s, current: provider };
          }),
        }));
      } else {
        toast.error(res.message || "Switch failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to switch provider");
    } finally { setSwitching(null); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testVTUProvider();
      if (res.status) {
        setTestResult(res.data);   // now { airtime: {ok,provider,planCount,responseMs}, data: {...}, ... }
        toast.success("Test complete");
      } else {
        toast.error(res.message || "Test failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Test failed");
    } finally { setTesting(false); }
  };

  const allProviderKeys = data?.allProviders?.map(p => p.key) || [];

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-white/5 rounded-xl animate-pulse" />
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-32 bg-[#161616] rounded-2xl border border-white/5 animate-pulse" />
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">VTU Providers</h2>
          <p className="text-white/40 text-xs mt-0.5">
            Assign a different provider per service — mix and match freely
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Set all services to one provider at once */}
          <select
            className="bg-[#161616] border border-white/10 rounded-xl px-3 py-2 text-xs text-white/60 focus:outline-none focus:border-white/20"
            defaultValue=""
            onChange={e => {
              if (e.target.value) {
                handleSwitch("all", e.target.value);
                e.target.value = "";
              }
            }}>
            <option value="" disabled>Set all to…</option>
            {allProviderKeys.map(k => (
              <option key={k} value={k}>{PROVIDER_META[k]?.label || k}</option>
            ))}
          </select>

          <button onClick={handleTest} disabled={testing}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/8 rounded-xl text-xs text-white/60 hover:text-white transition-colors disabled:opacity-40">
            {testing
              ? <><div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />Testing…</>
              : <><i className="ri-pulse-line" />Test All</>}
          </button>
        </div>
      </div>

      {/* Per-service test results */}
      {testResult && <TestPanel result={testResult} onClose={() => setTestResult(null)} />}

      {/* Per-service provider rows */}
      <div className="space-y-3">
        {(data?.services || []).map(svc => (
          <ServiceRow
            key={svc.service}
            service={svc.service}
            current={svc.current}
            options={svc.options}
            allProviderKeys={allProviderKeys}
            onSwitch={handleSwitch}
            isSwitching={switching === svc.service}
          />
        ))}
      </div>

      {/* Env vars reminder — now includes AirtimeNigeria */}
      <div className="bg-white/3 border border-white/5 rounded-2xl p-5">
        <p className="text-white/40 text-[10px] uppercase tracking-wide font-bold mb-3">
          Required Environment Variables
        </p>
        <div className="space-y-3">
          {[
            { provider: "VTPass",         vars: ["VTPASS_API_KEY", "VTPASS_SECRET_KEY"] },
            { provider: "Paygold",        vars: ["PAYGOLD_API_KEY", "PAYGOLD_BASE_URL (opt)"] },
            { provider: "VTU.ng",         vars: ["VTUNG_USERNAME", "VTUNG_PASSWORD", "VTUNG_BASE_URL (opt)"] },
            { provider: "AirtimeNigeria", vars: ["AIRTIMENIGERIA_API_TOKEN", "AIRTIMENIGERIA_CALLBACK_URL (opt)"] },
            { provider: "Strowallet",         vars: ["STROWALLET_PUBLIC_KEY", "STROWALLET_BASE_URL (opt)"] },
          ].map(({ provider, vars }) => (
            <div key={provider} className="flex items-start gap-3">
              <span className="text-white/40 text-xs font-semibold w-32 flex-shrink-0">{provider}</span>
              <div className="flex flex-wrap gap-1.5">
                {vars.map(v => (
                  <code key={v} className="text-[10px] px-2 py-0.5 rounded-lg bg-white/5 text-[#00C896] font-mono">{v}</code>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
